// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, WrappedComponentProps, injectIntl, FormattedMessage } from "react-intl";
import { OptionProps, components } from "react-select";

import { DashboardPanel, TimePeriod } from "types";
import { IAction, ActionType, raiseAction, IActionTypeSelectIos } from "actions";
import { IInput, InputType, IOutput, OutputMode } from "parameter";
import { IState } from "states/states";
import { GraphViewMode } from "states/cachedstates";
import { IDropdownSelectOption, SELECT_ALL_OPTION_ITEMS } from "view/comp/DropdownSelect";
import {
  getColor,
  getCustomPeriodAggregation,
  getGraphBoundaryDates,
  getIoType,
  getPanelSelection,
  getSiteSelection,
  getTrendIoGraphViewMode,
  toDropdownItems,
} from "guiutils";

import Dropdown from "view/comp/Dropdown";
import DropdownSelect, { allOption } from "view/comp/DropdownSelect";
import PeriodSelect from "view/comp/PeriodSelect";
import IoGraph from "view/comp/IoGraph";
import IoGraphCount from "view/comp/IoGraphCount";
import TrendHeading from "view/comp/TrendHeading";
import IoDatatable from "view/comp/IoDatatable";
import { isSet } from "utils";
import { Aggregation, AggregationType } from "graphql/api";

// uses webpack definitions
declare const IS_CLOUD: boolean;

interface IIoTrendExtProps {
  panel?: DashboardPanel;
  dashboardIndex?: number;
}
interface IIoTrendProps {
  inputs: IInput[] | undefined;
  outputs: IOutput[] | undefined;
  selectedInputs: number[] | undefined;
  selectedOutputs: number[] | undefined;
  isFullTrendAvailable: boolean;
  timePeriod: TimePeriod;
  graphViewMode: GraphViewMode;
  aggregation: Aggregation;
  selectedIoType: "analog" | "digital";
  selectedAggregation: AggregationType;
}

const mapStateToProps = (state: IState, ownProps: IIoTrendExtProps) => {
  const { index, selection } = getPanelSelection(
    state,
    ownProps.panel ?? DashboardPanel.ENERGY,
    ownProps.dashboardIndex,
  );
  const selectedIoType = getIoType(state);
  const graphDates = getGraphBoundaryDates(
    state.gui.ioPeriod.startDate,
    state.gui.ioPeriod.endDate,
    state.site.tzOffset,
    state.gui.ioPeriod.timePeriod,
  );
  const aggregation = getCustomPeriodAggregation(
    state.gui.ioPeriod.timePeriod,
    graphDates.startDate,
    graphDates.endDate,
  );
  const graphViewMode =
    getTrendIoGraphViewMode(
      selection,
      selectedIoType,
      Object.keys(state.trending.ioTrendFull).length > 0,
      false,
    ) ?? GraphViewMode.BAR;
  const siteSelection = getSiteSelection(state);

  const props: IIoTrendProps = {
    inputs: state.parameter?.input,
    outputs: state.parameter?.output,
    selectedInputs: siteSelection?.inputs,
    selectedOutputs: siteSelection?.outputs,
    timePeriod: state.gui.ioPeriod.timePeriod,
    isFullTrendAvailable: Object.keys(state.trending.ioTrendFull).length > 0,
    graphViewMode,
    aggregation,
    selectedIoType,
    selectedAggregation: state.gui.selectedIoAggregationType ?? AggregationType.AVG,
  };
  return props;
};

interface IIoTrendDispatch {
  onTypeDropdownChanged: (value: "digital" | "analog") => void;
  onAggregationDropdownChanged: (value: AggregationType) => void;
  onDropdownIOSelectionChanged: (
    type: "digital" | "analog",
    inputIds: number[],
    outputIds: number[],
  ) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IIoTrendDispatch = {
    onTypeDropdownChanged: (value) => {
      dispatch(
        raiseAction(ActionType.ACT_EDIT_VALUE, {
          name: "selectedIoType",
          value,
        }),
      );
    },
    onAggregationDropdownChanged: (value) => {
      dispatch(
        raiseAction(ActionType.ACT_EDIT_VALUE, {
          name: "selectedIoAggregationType",
          value,
        }),
      );
      dispatch(raiseAction(ActionType.ACT_UPDATE_IOTREND));
    },
    onDropdownIOSelectionChanged: (type, inputIds, outputIds) => {
      const payload: IActionTypeSelectIos = { type, inputIds, outputIds };
      dispatch(raiseAction(ActionType.ACT_SELECT_IOS, payload));
    },
  };
  return dispatchProps;
};

export function getAllAvailableIos(
  sourceArrays: (IOutput | IInput)[][],
  selectedIoType: "analog" | "digital",
) {
  const allOptions: IDropdownSelectOption[][] = [];
  sourceArrays.forEach((curSource, srcIndex) => {
    const indexes = Array.from(Array(curSource.length).keys())
      .filter(
        (curId) =>
          curSource[curId].webActive &&
          ((selectedIoType === "analog" &&
            ((curSource[curId] as IInput)?.type === InputType.ANALOG ||
              (curSource[curId] as IOutput)?.mode === OutputMode.ANALOG)) ||
            (selectedIoType === "digital" &&
              (((curSource[curId] as IOutput)?.mode ?? OutputMode.ANALOG) !== OutputMode.ANALOG ||
                ((curSource[curId] as IInput)?.type ?? InputType.ANALOG) !== InputType.ANALOG))),
      )
      .sort((a, b) => curSource[b].name.localeCompare(curSource[a].name));

    const items: IDropdownSelectOption[] = indexes.map((curIndex) => {
      return {
        value: (srcIndex === 0 ? "input" : "output") + String(curIndex),
        index: curIndex,
        id: curSource[curIndex].id ?? curIndex,
        source: srcIndex === 0 ? "input" : "output",
        visible: true,
        label: curSource[curIndex].name,
      } as IDropdownSelectOption;
    });
    allOptions.push(items);
  });
  return allOptions;
}

export function getSelectedIos(
  allIos: IDropdownSelectOption[][],
  selectedInputIds: number[] | undefined,
  selectedOutputIds: number[] | undefined,
  setInvisibleOnly: boolean,
) {
  // we face 2 approaches:
  // we may only extract the selected items or we may also add the invisible items
  const selectedInputs = allIos[0]
    .filter(
      (curItem) =>
        setInvisibleOnly || !isSet(selectedInputIds) || selectedInputIds.includes(curItem.id),
    )
    .map((curSelectOption) => {
      return {
        ...curSelectOption,
        visible:
          !setInvisibleOnly ||
          !isSet(selectedInputIds) ||
          selectedInputIds.includes(curSelectOption.id),
      };
    });
  const selectedOutputs = allIos[1]
    .filter(
      (curItem) =>
        setInvisibleOnly || !isSet(selectedOutputIds) || selectedOutputIds.includes(curItem.id),
    )
    .map((curSelectOption) => {
      return {
        ...curSelectOption,
        visible:
          !setInvisibleOnly ||
          !isSet(selectedOutputIds) ||
          selectedOutputIds.includes(curSelectOption.id),
      };
    });
  return [
    selectedInputs.sort((a, b) => a.label.localeCompare(b.label)),
    selectedOutputs.sort((a, b) => a.label.localeCompare(b.label)),
  ];
}

const messages = defineMessages({
  typeSelectCaption: {
    id: "Select analog/switch",
  },
  aggregationSelectCaption: {
    id: "Select data aggregation",
  },
  graphTitle: {
    id: "TrendGraphTitle",
    defaultMessage: "Trend",
  },
  timeGraphTitle: {
    id: "TrendTimeGraphTitle",
    defaultMessage: "Switching operations",
  },
  ioSelectionAllSelected: {
    id: "All items selected",
  },
  ioSelectionSomeItemsSelected: {
    id: "items selected",
  },
  inputs: {
    id: "Inputs",
  },
  outputs: {
    id: "Outputs",
  },
});

class IoTrendComponent extends React.Component<
  IIoTrendProps & IIoTrendDispatch & WrappedComponentProps
> {
  public render() {
    const hasDigitalIos =
      this.props.outputs?.some(
        (curItem) => curItem.webActive && curItem.mode !== OutputMode.ANALOG,
      ) ||
      this.props.inputs?.some((curItem) => curItem.webActive && curItem.type !== InputType.ANALOG);
    const hasAnalogIos =
      this.props.outputs?.some(
        (curItem) => curItem.webActive && curItem.mode === OutputMode.ANALOG,
      ) ||
      this.props.inputs?.some((curItem) => curItem.webActive && curItem.type === InputType.ANALOG);

    return (
      <div id="iotrend" className="flex-self flex-content">
        <div className="headerbuttons mb-2">
          <div className="left">
            {hasAnalogIos && hasDigitalIos && this.renderTypeSelect()}
            {this.renderIoSelect()}
            {this.props.selectedIoType === "analog" &&
              this.props.aggregation !== Aggregation.NONE &&
              this.renderAggregation()}
          </div>
          {IS_CLOUD && (
            <PeriodSelect identifier="ioPeriod" updateAction={ActionType.ACT_UPDATE_IOTREND} />
          )}
        </div>
        {this.renderTrendHeading()}
        {this.props.graphViewMode === GraphViewMode.TABLE ? (
          <IoDatatable aggregation={this.props.aggregation} />
        ) : this.props.selectedIoType === "analog" ||
          this.props.graphViewMode === GraphViewMode.DIGITAL ? (
          // IO graph is used for digital and analog view
          <IoGraph aggregation={this.props.aggregation} />
        ) : (
          // used for bar and line views (aggregated digital)
          <IoGraphCount />
        )}
      </div>
    );
  }

  private renderTypeSelect() {
    const getLabel = (value: "digital" | "analog") =>
      value === "digital" ? <FormattedMessage id="Switches" /> : <FormattedMessage id="Analog" />;
    const items = toDropdownItems(
      ["digital", "analog"],
      "ioType",
      (name, value) => this.props.onTypeDropdownChanged(value as "digital" | "analog"),
      getLabel,
    );

    return (
      <div className="me-2" id="typeselect">
        <Dropdown
          items={items}
          caption={this.props.intl.formatMessage(messages.typeSelectCaption)}
          curItem={getLabel(this.props.selectedIoType)}
        />
      </div>
    );
  }

  private renderAggregation() {
    const getLabel = (value: AggregationType) =>
      value === AggregationType.MIN ? (
        <FormattedMessage id="Minimum" />
      ) : value === AggregationType.MAX ? (
        <FormattedMessage id="Maximum" />
      ) : (
        <FormattedMessage id="Average" />
      );
    const items = toDropdownItems(
      [AggregationType.AVG, AggregationType.MIN, AggregationType.MAX],
      "ioAggregation",
      (name, value) => this.props.onAggregationDropdownChanged(value),
      getLabel,
    );

    return (
      <div className="me-2" id="aggregationselect">
        <Dropdown
          items={items}
          caption={this.props.intl.formatMessage(messages.aggregationSelectCaption)}
          curItem={getLabel(this.props.selectedAggregation)}
        />
      </div>
    );
  }

  private renderTrendHeading() {
    const showGraphModes: GraphViewMode[] = [];
    if (this.props.selectedIoType === "digital" && !this.props.isFullTrendAvailable) {
      showGraphModes.push(GraphViewMode.BAR);
    }
    if (this.props.selectedIoType === "analog") {
      showGraphModes.push(GraphViewMode.LINE);
    }
    if (this.props.selectedIoType === "digital" && this.props.isFullTrendAvailable) {
      showGraphModes.push(GraphViewMode.DIGITAL);
    }
    if (this.props.selectedIoType === "analog" || this.props.isFullTrendAvailable) {
      showGraphModes.push(GraphViewMode.TABLE);
    }

    return (
      <TrendHeading
        identifier="ioPeriod"
        includeDate={true}
        showGraphModes={showGraphModes}
        title={this.props.intl.formatMessage(
          this.props.selectedIoType === "digital" ? messages.timeGraphTitle : messages.graphTitle,
        )}
      />
    );
  }

  private optionsCallback(props: OptionProps<IDropdownSelectOption, boolean, any>) {
    return (
      <div
        style={{
          backgroundColor: "rgba(0,0,0,0)",
        }}
      >
        <components.Option {...props}>
          <input
            style={{
              accentColor:
                props.data.index === SELECT_ALL_OPTION_ITEMS
                  ? "rgba(1,1,1,1)"
                  : getColor(props.data.index),
            }}
            type="checkbox"
            checked={props.isSelected}
            onChange={() => null}
          />{" "}
          <label
            style={{
              color:
                props.data.index === SELECT_ALL_OPTION_ITEMS
                  ? "rgba(1,1,1,1)"
                  : getColor(props.data.index),
            }}
          >
            {props.data.index !== SELECT_ALL_OPTION_ITEMS
              ? props.data.label
              : this.props.intl.formatMessage({ id: props.data.label as string })}
          </label>
        </components.Option>
      </div>
    );
  }

  private renderIoSelect() {
    const allOptions = getAllAvailableIos(
      [this.props.inputs ?? [], this.props.outputs ?? []],
      this.props.selectedIoType,
    );
    const groupedOptions = [
      { label: this.props.intl.formatMessage(messages.inputs), options: allOptions[0] },
      { label: this.props.intl.formatMessage(messages.outputs), options: allOptions[1] },
    ];
    const selectedOptions = getSelectedIos(
      allOptions,
      this.props.selectedInputs,
      this.props.selectedOutputs,
      false,
    );
    const selectedOptionsFlat = [...selectedOptions[0], ...selectedOptions[1]];
    const isAllSelected =
      selectedOptions[0].length === allOptions[0].length &&
      selectedOptions[1].length === allOptions[1].length;
    if (isAllSelected) {
      selectedOptionsFlat.push(allOption);
    }

    return (
      <div className="me-2" id="ioselect">
        <DropdownSelect
          options={groupedOptions}
          value={selectedOptionsFlat}
          noOptionsString={<FormattedMessage id="No input/output available" />}
          components={{ Option: (props) => this.optionsCallback(props) }}
          isMulti={true}
          closeMenuOnSelect={false}
          placeholder={
            <div>
              {selectedOptionsFlat.length > 0 ? (
                isAllSelected ? (
                  this.props.intl.formatMessage(messages.ioSelectionAllSelected)
                ) : selectedOptionsFlat.length > 1 ? (
                  String(selectedOptionsFlat.length) +
                  " " +
                  this.props.intl.formatMessage(messages.ioSelectionSomeItemsSelected)
                ) : selectedOptionsFlat.length === 1 ? (
                  selectedOptionsFlat[0].label
                ) : (
                  <FormattedMessage id="Select input/output" />
                )
              ) : (
                <FormattedMessage id="No input/output available" />
              )}
            </div>
          }
          onChange={(value: IDropdownSelectOption[]) =>
            this.props.onDropdownIOSelectionChanged(
              this.props.selectedIoType,
              value.filter((curItem) => curItem.source === "input").map((curItem) => curItem.id),
              value.filter((curItem) => curItem.source === "output").map((curItem) => curItem.id),
            )
          }
          allowSelectAll={allOptions.length > 0}
        />
      </div>
    );
  }
}

const IoTrend = connect(mapStateToProps, mapDispatchToProps)(IoTrendComponent);

export default injectIntl(IoTrend);
